import { useMemo } from 'react';
import { useDialer } from '@/contexts/DialerContext';
import { useLiveCallContact, type LiveCallContact } from '@/hooks/useLiveCallContact';
import { useNow } from '@/hooks/useNow';
import { useCallPageStore, type Countdown, type QualifyingCall, type SavedQualification, type SessionSummary } from '@/state/callPageStore';
import { HUMAN_ONLY_OUTCOMES, reachedPerson, toLineViews, type CallLineView } from '@/services/dialer/callPage/lines';
import type { DialerContextValue } from '@/contexts/DialerContextTypes';
import type { CallPagePhase } from '@/services/dialer/callPage/keyboard';

/**
 * The call page's read side (spec §6). It subscribes to what already exists
 * (the dialer contexts, the call pool through useLiveCallContact, the
 * inbound session) and to the page store; it creates no pool and no
 * session of its own (SR3-1). useCallCommands is the only writer.
 */

export interface AudibleCall {
  parallelCallId: string | null;
  prospectId: string;
  name: string;
  phone: string;
  company: string;
  startedAt: number | null;
  inbound: boolean;
}

export interface CallSessionView {
  phase: CallPagePhase;
  lines: CallLineView[];
  audible: AudibleCall | null;
  qualifying: QualifyingCall | null;
  consultedPhone: string | null;
  countdown: (Countdown & { remainingMs: number; canUndo: boolean }) | null;
  device: 'ready' | 'offline' | 'reconnecting' | 'mic_denied';
  missedInbound: number;
  isMuted: boolean;
  keypadOpen: boolean;
  sessionStartedAt: number | null;
  remaining: number;
  stats: { calls: number; connected: number; meetings: number; callbacks: number };
  saved: SavedQualification[];
  summary: SessionSummary | null;
  /** Calls of this session a person answered and nobody qualified. */
  unqualified: Array<{ id: string; name: string }>;
}

interface Row {
  id?: string;
  prospect_name?: string | null;
  prospect_phone?: string | null;
  call_status?: string | null;
  call_outcome?: string | null;
  direction?: string | null;
  agent_email?: string | null;
  meeting_booked?: boolean | null;
  connected_at?: string | null;
  agent_bridged_at?: string | null;
  amd_result?: string | null;
  human_qualified_at?: string | null;
  created_date?: string | null;
  started_at?: string | null;
  updated_date?: string | null;
}

const EMPTY: Row[] = [];
/**
 * The session clock starts when the first dial answers (dialerRunState goes
 * 'running' in its onSuccess), just after the first series' calls were
 * created: count them in.
 */
const SESSION_GRACE_MS = 60_000;

function toAudible(c: LiveCallContact | null, inbound: boolean): AudibleCall | null {
  if (!c) return null;
  const started = c.callStartedAt ? new Date(c.callStartedAt as string).getTime() : null;
  return {
    parallelCallId: (c.callId as string | null | undefined) ?? null,
    prospectId: String(c.prospect_id ?? c.id ?? ''),
    name: String(c.prospect_name ?? c.name ?? ''),
    phone: String(c.prospect_phone ?? c.phone ?? ''),
    company: String(c.prospect_company ?? c.company ?? ''),
    startedAt: started && !Number.isNaN(started) ? started : null,
    inbound,
  };
}

export function useCallSessionView(): CallSessionView {
  const d = useDialer() as Pick<DialerContextValue,
    'activeCalls' | 'allCalls' | 'dialerRunState' | 'deviceReady' | 'deviceState' | 'micPermissionGranted'
    | 'isMuted' | 'showKeypad' | 'dialingSessionStartedAt' | 'callerData' | 'stats'>;
  const live = useLiveCallContact();
  const store = useCallPageStore();
  const now = useNow(1000);

  // The stored call rows, read with the fields this page uses.
  const activeCalls = (d.activeCalls ?? EMPTY) as Row[];
  const allCalls = (d.allCalls ?? EMPTY) as Row[];
  const sessionStartedAt = d.dialingSessionStartedAt ?? null;
  const me = d.callerData?.caller_email ?? '';

  const audible = toAudible(live.connectedContact, live.isInboundLive);
  // An answered inbound call carries its ParallelCall in the inbound session.
  if (audible && live.isInboundLive && !audible.parallelCallId) {
    audible.parallelCallId = (live.inbound as { parallelCallId?: string | null }).parallelCallId ?? null;
  }
  const audibleId = audible?.parallelCallId ?? null;

  // The session's calls: this caller's, since the session clock started.
  const sessionCalls = useMemo(() => {
    if (!sessionStartedAt) return EMPTY;
    return allCalls.filter((c) => {
      const at = Date.parse(c.created_date ?? '');
      return !Number.isNaN(at) && at >= sessionStartedAt - SESSION_GRACE_MS && (!me || !c.agent_email || c.agent_email === me);
    });
  }, [allCalls, sessionStartedAt, me]);

  const lines = useMemo(() => {
    const seen = new Set<string>();
    const rows = [...activeCalls, ...sessionCalls.slice(0, 6)].filter((r) => {
      if (!r.id || seen.has(r.id) || r.direction === 'inbound') return false;
      seen.add(r.id);
      return true;
    });
    return toLineViews(rows, audibleId, now);
  }, [activeCalls, sessionCalls, audibleId, now]);

  const stats = useMemo(() => {
    return {
      calls: sessionCalls.length,
      connected: sessionCalls.filter(reachedPerson).length,
      meetings: sessionCalls.filter((c) => c.meeting_booked === true).length,
      callbacks: sessionCalls.filter((c) => c.call_outcome === 'callback_requested').length,
    };
  }, [sessionCalls]);

  const unqualified = useMemo(() => {
    const saved = new Set(store.saved.map((s) => s.parallelCallId));
    return sessionCalls
      .filter((c) => c.call_status === 'completed' && !c.human_qualified_at && !saved.has(c.id ?? '')
        && reachedPerson(c) && !HUMAN_ONLY_OUTCOMES.includes(c.call_outcome ?? '') && c.meeting_booked !== true
        && c.id !== audibleId)
      .map((c) => ({ id: c.id!, name: c.prospect_name || c.prospect_phone || '' }));
  }, [sessionCalls, store.saved, audibleId]);

  // Inbound calls that rang while the caller was on another call: the engine
  // ignores them (spec §4.4), the server keeps them.
  const missedInbound = useMemo(
    () => sessionCalls.filter((c) => c.direction === 'inbound' && !c.agent_bridged_at && c.call_status !== 'connected_to_agent').length,
    [sessionCalls],
  );

  const device: CallSessionView['device'] = d.micPermissionGranted === false
    ? 'mic_denied'
    : d.deviceReady ? 'ready' : d.deviceState === 'registering' ? 'reconnecting' : 'offline';

  const countdown = store.countdown
    // Read the clock here: the ticking `now` can lag by its interval, which
    // showed « 4 s » for a 3 s countdown and offered « Annuler » too late.
    ? { ...store.countdown, remainingMs: Math.max(0, store.countdown.endsAt - Date.now()), canUndo: Date.now() < store.countdown.undoUntil }
    : null;

  let phase: CallPagePhase;
  if (store.summary) phase = 'finished';
  // A call on the line always wins: an inbound one answered while another
  // waits for its outcome (CALL-PILL-1), or an outbound one while a call from
  // the summary or the reconciliation is being qualified.
  else if (audible && live.isLive && !live.callEnded) phase = 'connected';
  else if (store.qualifying || (audible && live.callEnded)) phase = 'qualifying';
  else if (audible && live.isLive) phase = 'connected';
  else if (d.dialerRunState === 'paused') phase = 'paused';
  else if (d.dialerRunState === 'running') phase = 'dialing';
  else phase = 'prep';

  // A remote hang-up leaves the machine in `qualified` with its contact: the
  // page qualifies that call, same as after its own hang-up.
  const qualifying: QualifyingCall | null = store.qualifying ?? (phase === 'qualifying' && audible?.parallelCallId
    ? { parallelCallId: audible.parallelCallId, prospectId: audible.prospectId, name: audible.name, phone: audible.phone, company: audible.company, endedAt: now }
    : null);

  return {
    phase,
    lines,
    audible: phase === 'connected' ? audible : null,
    qualifying,
    consultedPhone: store.consultedPhone,
    countdown,
    device,
    missedInbound,
    isMuted: !!d.isMuted,
    keypadOpen: !!d.showKeypad,
    sessionStartedAt,
    remaining: d.stats?.queuedProspects ?? 0,
    stats,
    saved: store.saved,
    summary: store.summary,
    unqualified,
  };
}
